import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore'
import { Research, ResearchTag } from '../types/Research'
import { getEffectiveServices } from './firebaseServiceContext'

// Research collection helpers
export const researchCollection = () =>
  collection(getEffectiveServices().firestore, 'research')
export const researchDoc = (id: string) =>
  doc(getEffectiveServices().firestore, 'research', id)

// Research tag collection helpers
export const researchTagsCollection = () =>
  collection(getEffectiveServices().firestore, 'researchTags')
export const researchTagDoc = (id: string) =>
  doc(getEffectiveServices().firestore, 'researchTags', id)

// Fetch all research articles
export const fetchResearch = async (): Promise<Research[]> => {
  const q = query(researchCollection(), orderBy('title', 'asc'))
  const querySnapshot = await getDocs(q)
  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      } as Research)
  )
}

// Fetch research by ID
export const fetchResearchById = async (
  id: string
): Promise<Research | null> => {
  const docSnap = await getDoc(researchDoc(id))
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Research
  }
  return null
}

// Fetch all research tags
export const fetchResearchTags = async (): Promise<ResearchTag[]> => {
  const q = query(researchTagsCollection(), orderBy('name', 'asc'))
  const querySnapshot = await getDocs(q)
  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      } as ResearchTag)
  )
}

// Add new research article
export const addResearch = async (
  research: Omit<Research, 'id'>
): Promise<string> => {
  try {
    const docRef = await addDoc(researchCollection(), research)
    return docRef.id
  } catch (error) {
    console.error('Firestore error adding research:', error)
    throw new Error(
      `Failed to create research article: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

// Update research article
export const updateResearch = async (
  id: string,
  updates: Partial<Research>
): Promise<void> => {
  await updateDoc(researchDoc(id), { ...updates, updatedAt: Timestamp.now() })
}

// Delete research article
export const deleteResearch = async (id: string): Promise<void> => {
  await deleteDoc(researchDoc(id))
}

// Add new research tag
export const addResearchTag = async (
  tag: Omit<ResearchTag, 'id'>
): Promise<string> => {
  try {
    console.log('Attempting to add research tag to Firestore:', tag)
    console.log('Collection path:', 'researchTags')

    const docRef = await addDoc(researchTagsCollection(), tag)
    console.log('Research tag added successfully with ID:', docRef.id)

    return docRef.id
  } catch (error) {
    console.error('Firestore error adding research tag:', error)
    console.error('Error type:', typeof error)
    console.error(
      'Error name:',
      error instanceof Error ? error.name : 'Unknown'
    )
    console.error('Error code:', (error as any)?.code)

    throw new Error(
      `Failed to create tag: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

// Search research articles
export const searchResearch = async (
  searchTerm: string,
  selectedTags: string[] = []
): Promise<Research[]> => {
  // For now, we'll fetch all research and filter client-side
  // In a production app, you might want to use Algolia or similar for better search
  const allResearch = await fetchResearch()

  return allResearch.filter((research) => {
    const searchLower = searchTerm.toLowerCase()
    const matchesSearch =
      !searchTerm ||
      research.title.toLowerCase().includes(searchLower) ||
      research.abstract.toLowerCase().includes(searchLower) ||
      research.tags.some((tag) => tag.toLowerCase().includes(searchLower))

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => research.tags.includes(tag))

    return matchesSearch && matchesTags
  })
}

// Filter research by first letter of title
export const filterResearchByLetter = (
  research: Research[],
  letter: string
): Research[] => {
  if (!letter) return research
  return research.filter((r) => r.title.charAt(0).toUpperCase() === letter)
}
